import { useEffect, useRef } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, Search, Activity, Zap, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SortableHeaderButton } from '@/components/design-system/table/SortableHeaderButton'
import { StatCard } from '@/components/design-system/StatCard'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import TelemetryDateFilter from '../components/TelemetryDateFilter'
import TelemetryPagination from '../components/TelemetryPagination'
import ActivityTimelineChart from '../components/ActivityTimelineChart'
import { telemetryPathWithParams, telemetryRangeParams, useTelemetryNavigation, useSearchQuery } from '../hooks/useTelemetryNavigation'
import { formatDate, formatNumber, formatPercentage, statusBadge } from '../utils'
import TokensWithBreakdown from '../components/TokensWithBreakdown'
import { remainingPercent } from '@/frontend/utils/tokenUsage'
import type { DateRange, Pagination, OrganizationRef, TimelinePoint, TokenBreakdown } from '../types'

type UsageStatus = 'ok' | 'warning' | 'critical'

interface WorkerBillingUsage {
  hasPlan: boolean
  status: UsageStatus
  percent: number | null
}

interface DigitalWorker {
  id: string
  name: string
  instanceName: string
  fleetProfile: string | null
  provisioningStatus: string
  sessionsCount: number
  aggregateTokens: number
  latestActivity: string | null
  tokenBreakdown: TokenBreakdown | null
  billingUsage: WorkerBillingUsage
}

function WorkerUsageBadge({ usage }: { usage: WorkerBillingUsage }) {
  if (!usage.hasPlan) {
    return <span className="text-muted-foreground">—</span>
  }
  if (usage.percent === null) {
    return <Badge variant="success">Ilimitado</Badge>
  }
  const tone: 'success' | 'warning' | 'critical' =
    usage.status === 'critical' ? 'critical' : usage.status === 'warning' ? 'warning' : 'success'
  const availablePercent = remainingPercent(usage.percent)
  return (
    <Badge variant={tone}>
      {formatPercentage(availablePercent ?? 0)}% disponível hoje
    </Badge>
  )
}

interface Stats {
  totalWorkers: number
  totalSessions: number
  totalTokens: number
  activeWorkers: number
  tokenBreakdown: TokenBreakdown | null
}

interface Props {
  organization: OrganizationRef
  stats: Stats
  digitalWorkers: DigitalWorker[]
  pagination: Pagination
  search: string
  dateFrom: string
  dateTo: string
  dateRange: DateRange
  sortBy: string
  sortDirection: 'asc' | 'desc'
  activityTimeline: TimelinePoint[]
}

function OrganizationShow({ organization, stats, digitalWorkers, pagination, search, dateFrom, dateTo, dateRange, sortBy, sortDirection, activityTimeline }: Props) {
  const [query, setQuery] = useSearchQuery(search)
  const normalizingSortRef = useRef(false)
  const basePath = `/cc/telemetry/organizations/${organization.slug}`
  const rangeParams = telemetryRangeParams(dateFrom, dateTo, dateRange)
  const { navigateWithParams, handleSort, handleDateChange } = useTelemetryNavigation(basePath, {
    search: search || undefined,
    ...rangeParams,
    sort_by: sortBy,
    sort_direction: sortDirection,
    page: pagination.page,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigateWithParams({ search: query || undefined, page: 1 })
  }

  const onSort = (key: string) => handleSort(sortBy, sortDirection, key)

  useEffect(() => {
    if (sortBy !== 'aggregate_cost') {
      normalizingSortRef.current = false
      return
    }
    if (normalizingSortRef.current) return

    normalizingSortRef.current = true
    const params = new URLSearchParams(window.location.search)
    params.set('sort_by', 'latest_activity')
    params.set('sort_direction', 'desc')
    params.set('page', '1')
    window.location.replace(`${basePath}?${params.toString()}`)
  }, [basePath, sortBy])

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Link href={telemetryPathWithParams('/cc/telemetry', rangeParams)} className="text-muted-foreground hover:text-muted-foreground transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
        <h2 className="text-2xl font-bold text-foreground">{organization.name}</h2>
      </div>

      <div className="mb-4">
        <TelemetryDateFilter dateFrom={dateFrom} dateTo={dateTo} dateRange={dateRange} onChange={handleDateChange} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <StatCard label="Digital Workers" value={formatNumber(stats.totalWorkers)} icon={<Users />} colorScheme="gray" size="small" />
        <StatCard label="Sessões" value={formatNumber(stats.totalSessions)} icon={<Activity />} colorScheme="green" size="small" />
        <StatCard label="Tokens" value={<TokensWithBreakdown total={stats.totalTokens} breakdown={stats.tokenBreakdown} />} icon={<Zap />} colorScheme="blue" size="small" />
      </div>

      <div className="mb-6">
        <ActivityTimelineChart data={activityTimeline} title="Sessões por dia" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">{pagination.total} digital workers</span>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome ou instância..." className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72" />
          </div>
          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-580px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Instância</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Sessões" sortKey="sessions_count" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Tokens" sortKey="aggregate_tokens" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Uso</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Última Atividade" sortKey="latest_activity" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {digitalWorkers.map((worker) => (
                <tr key={worker.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3"><Link href={telemetryPathWithParams(`/cc/telemetry/digital_workers/${worker.id}`, rangeParams)} className="text-sm font-medium text-foreground hover:text-blue-600">{worker.name}</Link></td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-muted-foreground">{worker.instanceName}</span></td>
                  <td className="px-4 py-3">{statusBadge(worker.provisioningStatus)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{worker.sessionsCount}</td>
                  <td className="px-4 py-3 text-sm text-foreground"><TokensWithBreakdown total={worker.aggregateTokens} breakdown={worker.tokenBreakdown} /></td>
                  <td className="px-4 py-3 text-sm"><WorkerUsageBadge usage={worker.billingUsage} /></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{worker.latestActivity ? formatDate(worker.latestActivity) : '—'}</td>
                </tr>
              ))}
              {digitalWorkers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum digital worker encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <TelemetryPagination pagination={pagination} onPageChange={(page) => navigateWithParams({ page })} />
      </div>
    </div>
  )
}

OrganizationShow.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default OrganizationShow
